import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { TIPO_ORIGEN } from 'src/components/views/EscritosLexnet/components/IniciadorAsunto/components/Origen/consts'
import {
  regexDecimals,
  regexFileName,
  regexNumberOrDot,
  regexUnlimitedDecimals,
  regexYear,
  regexNumber,
  regexExcludedCharacters
} from 'src/components/views/EscritosLexnet/const'
import { MIN_DATE } from 'src/constants/time'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import {
  CautelarOpciones,
  DocumentosPermitidos,
  LiteralTiposOrden,
  ResolucionOpciones,
  TipoMensaje
} from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import { TipoCuantia } from 'src/interfaces/Catalog'
import { FileHashType, Interviniente } from 'src/interfaces/EscritosLegales'
// import { checkTipoPersonaFisica } from 'src/components/views/EscritosLexnet/components/shared/components/Intervinientes/components/types'
// import { addressValidationSchema } from 'src/components/views/EscritosLexnet/components/shared/components/Intervinientes/components/forms/AddressForm'
// import { validId } from 'src/components/views/EscritosLexnet/components/shared/components/Intervinientes/components/Interviniente/types'

const CUANTIA_LENGTH = 13

const validateLenghtFileName = (value: FileHashType) => {
  if (value && value.file && value.file.name) {
    return value.file.name.length <= 120
  }
  return true
}

const validateFileChars = (value: FileHashType) => {
  if (value && value.file && value.file.name) {
    if (value.file.name.match(regexFileName)) {
      return true
    }
    return false
  }
  return true
}

const validateFileHasExtension = (value: FileHashType) => {
  if (value && value.file && value.file.name) {
    return value.file.name.indexOf('.') !== -1
  }
  return true
}

const addOneDay = () => {
  const currentDate = new Date()
  currentDate.setDate(currentDate.getDate() + 1)
  return currentDate
}

function useEcritoLegalValidation(esCorrecion: boolean): any {
  const { t } = useTranslation()
  const { tipoMensaje, isMonitorio } = useTipoMensajeContext()

  const defaultRequiredText = t('form.validation.required.default')
  const principalExtNotValid = t('form.validation.principal_ext_not_valid')
  const principalWithExtension = t('form.validation.principal_has_extension')
  const anexoExtNotValid = t('form.validation.anexo_ext_not_valid')
  const FileNameLengthNotValid = t('form.validation.filename_length_not_valid')
  const FileNameCharsNotValid = t('form.validation.filename_chars_not_valid')
  const notSuperiorToCurrentDate = t('form.validation.not_superior_to_current_date')
  const mustBeNumber = t('form.validation.must_be_number')
  const mustBeYear = t('form.validation.must_be_year')
  const invalidTime = t('form.validation.invalid_time')
  const invalidDate = t('form.validation.invalid_date')
  const greaterOrEqualOne = t('form.validation.greater_or_equal_one')
  const excludedCharacters = t('form.validation.excluded_characters')

  function cuantiaReconvencionText(number: number) {
    return t('form.validation.cuantia_reconvencion', { number })
  }

  function unlimitedDecimals(number: number) {
    return t('form.validation.unlimited_decimals', { number })
  }

  function merge(...schemas) {
    const schemasFiltered = schemas.filter(x => x !== undefined)
    const [first, ...rest] = schemasFiltered
    return rest.reduce((mergedSchemas, schema) => mergedSchemas.concat(schema), first)
  }

  const adjuntos = () => {
    if (isMonitorio) {
      return Yup.object().shape({
        [FormField.DOCUMENTOS_ANEXOS]: Yup.array()
          .of(
            Yup.object().shape({
              subtipo: Yup.object()
                .nullable()
                .required(defaultRequiredText)
            })
          )
          .test(
            'hasPMSWhenMasc',
            t('form.validation.adjuntos_pms_required_monitorio'),
            function(value) {
              const masc = this.parent[FormField.DATOS_MASC]
              if (!masc) return true
              return (value ?? []).some(doc => (doc.subtipo as any)?.codigo === 'PMS')
            }
          ),
        [FormField.DOCUMENTOS_ANEXOS_ARCHIVO]: Yup.mixed()
          .test('v1', anexoExtNotValid, value => {
            let valid = true
            if (value) {
              value.forEach(doc => {
                if (
                  !DocumentosPermitidos.ANEXOS.includes(
                    doc.file.name
                      .toLowerCase()
                      .split('.')
                      .pop()
                  )
                ) {
                  valid = false
                }
              })
            }
            return valid
          })
          .test('v2', FileNameLengthNotValid, value => {
            let valid = true
            if (value) {
              value.forEach(doc => {
                if (!validateLenghtFileName(doc)) {
                  valid = false
                }
              })
            }
            return valid
          })
          .test('v3', FileNameCharsNotValid, value => {
            let valid = true
            if (value) {
              value.forEach(doc => {
                if (!validateFileChars(doc)) {
                  valid = false
                }
              })
            }
            return valid
          })
      })
    }
    if (!esCorrecion) {
      return Yup.object().shape({
        [FormField.DOCUMENTO_PRINCIPAL]: Yup.array().of(
          Yup.object().shape({
            subtipo: Yup.object()
              .nullable()
              .required(defaultRequiredText)
          })
        ),
        [FormField.DOCUMENTO_PRINCIPAL_ARCHIVO]: Yup.mixed()
          .test('v0', principalWithExtension, value => validateFileHasExtension(value))
          .test('v1', principalExtNotValid, value => {
            if (value && value.file && value.file.name) {
              return DocumentosPermitidos.PRINCIPAL.includes(
                value.file.name
                  .toLowerCase()
                  .split('.')
                  .pop()
              )
            }
            return true
          })
          .test('v2', FileNameLengthNotValid, value => validateLenghtFileName(value))
          .test('v3', FileNameCharsNotValid, value => validateFileChars(value)),
        [FormField.DOCUMENTOS_ANEXOS]: Yup.array().of(
          Yup.object().shape({
            subtipo: Yup.object()
              .nullable()
              .required(defaultRequiredText)
          })
        ),
        [FormField.DOCUMENTOS_ANEXOS_ARCHIVO]: Yup.mixed()
          .test('v1', anexoExtNotValid, value => {
            let valid = true
            if (value) {
              value.forEach(doc => {
                if (
                  !DocumentosPermitidos.ANEXOS.includes(
                    doc.file.name
                      .toLowerCase()
                      .split('.')
                      .pop()
                  )
                ) {
                  valid = false
                }
              })
            }
            return valid
          })
          .test('v2', FileNameLengthNotValid, value => {
            let valid = true
            if (value) {
              value.forEach(doc => {
                if (!validateLenghtFileName(doc)) {
                  valid = false
                }
              })
            }
            return valid
          })
          .test('v3', FileNameCharsNotValid, value => {
            let valid = true
            if (value) {
              value.forEach(doc => {
                if (!validateFileChars(doc)) {
                  valid = false
                }
              })
            }
            return valid
          })
      })
    }
    return undefined
  }

  // VALIDACIONES PROCEDIMIENTO
  const procedimiento = () => {
    switch (tipoMensaje) {
      case TipoMensaje.ESCRITO:
      case TipoMensaje.INICIADOR_EJECUCION:
        return Yup.object().shape({
          [FormField.PROCEDIMIENTO_DESCRIPCION]: Yup.string().when(
            [
              FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL,
              FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION
            ],
            {
              is: (deTituloJudicial: boolean, orden: string) => {
                orden = orden?.toUpperCase()
                return (
                  deTituloJudicial === false &&
                  (orden === LiteralTiposOrden.SOCIAL || orden === LiteralTiposOrden.CIVIL)
                )
              },
              then: Yup.string().notRequired(),
              otherwise: Yup.string().required(defaultRequiredText)
            }
          ),
          [FormField.PROCEDIMIENTO_REFERENCIA]: Yup.string().when(
            [
              FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL,
              FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION
            ],
            {
              is: (deTituloJudicial: boolean, orden: string) => {
                orden = orden?.toUpperCase()
                return (
                  deTituloJudicial === false &&
                  (orden === LiteralTiposOrden.SOCIAL || orden === LiteralTiposOrden.CIVIL)
                )
              },
              then: Yup.string().notRequired(),
              otherwise: Yup.string().required(defaultRequiredText)
            }
          )
        })
      case TipoMensaje.INICIADOR_ASUNTO:
        return Yup.object().shape({
          [FormField.PROCEDIMIENTO_DESCRIPCION]: Yup.string()
            .when([FormField.CAUTELAR], {
              is: (item: string) => item === CautelarOpciones.SI,
              then: Yup.string().required(defaultRequiredText)
            })
            .when([FormField.ORGANO_ORIGEN_DESCRIPCION], {
              is: (item: string) => item && item.length,
              then: Yup.string().required(' ')
            }),
          [FormField.PROCEDIMIENTO_REFERENCIA]: Yup.string()
            .when([FormField.CAUTELAR], {
              is: (item: string) => item === CautelarOpciones.SI,
              then: Yup.string().required(defaultRequiredText)
            })
            .when([FormField.ORGANO_ORIGEN_DESCRIPCION], {
              is: (item: string) => item && item.length,
              then: Yup.string().required(' ')
            })
        })
      case TipoMensaje.PERSONACION:
        return Yup.object().shape({
          [FormField.PROCEDIMIENTO_DESCRIPCION]: Yup.string().required(defaultRequiredText),
          [FormField.PROCEDIMIENTO_REFERENCIA]: Yup.string().required(defaultRequiredText)
        })
      default:
        return undefined
    }
  }

  // VALIDACIONES NIG
  const NIG = () => {
    switch (tipoMensaje) {
      case TipoMensaje.ESCRITO:
        return Yup.object().shape({
          [FormField.NIG_FULL_NUMBER]: Yup.string().min(19, t('form.validation.NIG')),
          [FormField.NIG_1]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .when([FormField.NIG_FULL_NUMBER], {
              is: (item: string) => item && item.length,
              then: Yup.string()
                .required(' ')
                .min(5, ' ')
            }),
          [FormField.NIG_2]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .when([FormField.NIG_FULL_NUMBER], {
              is: (item: string) => item && item.length,
              then: Yup.string()
                .required(' ')
                .min(2, ' ')
            }),
          [FormField.NIG_3]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .when([FormField.NIG_FULL_NUMBER], {
              is: (item: string) => item && item.length,
              then: Yup.string()
                .required(' ')
                .min(1, ' ')
            }),
          [FormField.NIG_4]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .when([FormField.NIG_FULL_NUMBER], {
              is: (item: string) => item && item.length,
              then: Yup.string()
                .required(' ')
                .min(4, ' ')
            }),
          [FormField.NIG_5]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .when([FormField.NIG_FULL_NUMBER], {
              is: (item: string) => item && item.length,
              then: Yup.string()
                .required(' ')
                .min(7, ' ')
            })
        })
      case TipoMensaje.INICIADOR_ASUNTO:
        return Yup.object().shape({
          [FormField.NIG_FULL_NUMBER]: Yup.string()
            .min(19, t('form.validation.NIG'))
            .when([FormField.ORGANO_ORIGEN_DESCRIPCION, FormField.CAUTELAR], {
              is: (descripcion: string, cautelar: CautelarOpciones) =>
                (descripcion && descripcion.length) || cautelar === CautelarOpciones.SI,
              then: Yup.string().required(' ')
            }),
          [FormField.NIG_1]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .when([FormField.ORGANO_ORIGEN_DESCRIPCION, FormField.CAUTELAR], {
              is: (descripcion: string, cautelar: CautelarOpciones) =>
                (descripcion && descripcion.length) || cautelar === CautelarOpciones.SI,
              then: Yup.string().required(' ')
            })
            .when([FormField.NIG_FULL_NUMBER], {
              is: (item: string) => item && item.length,
              then: Yup.string()
                .required(' ')
                .min(5, ' ')
            }),
          [FormField.NIG_2]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .when([FormField.ORGANO_ORIGEN_DESCRIPCION, FormField.CAUTELAR], {
              is: (descripcion: string, cautelar: CautelarOpciones) =>
                (descripcion && descripcion.length) || cautelar === CautelarOpciones.SI,
              then: Yup.string().required(' ')
            })
            .when([FormField.NIG_FULL_NUMBER], {
              is: (item: string) => item && item.length,
              then: Yup.string()
                .required(' ')
                .min(2, ' ')
            }),
          [FormField.NIG_3]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .when([FormField.ORGANO_ORIGEN_DESCRIPCION, FormField.CAUTELAR], {
              is: (descripcion: string, cautelar: CautelarOpciones) =>
                (descripcion && descripcion.length) || cautelar === CautelarOpciones.SI,
              then: Yup.string().required(' ')
            })
            .when([FormField.NIG_FULL_NUMBER], {
              is: (item: string) => item && item.length,
              then: Yup.string()
                .required(' ')
                .min(1, ' ')
            }),
          [FormField.NIG_4]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .when([FormField.ORGANO_ORIGEN_DESCRIPCION, FormField.CAUTELAR], {
              is: (descripcion: string, cautelar: CautelarOpciones) =>
                (descripcion && descripcion.length) || cautelar === CautelarOpciones.SI,
              then: Yup.string().required(' ')
            })
            .when([FormField.NIG_FULL_NUMBER], {
              is: (item: string) => item && item.length,
              then: Yup.string()
                .required(' ')
                .min(4, ' ')
            }),
          [FormField.NIG_5]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .when([FormField.ORGANO_ORIGEN_DESCRIPCION, FormField.CAUTELAR], {
              is: (descripcion: string, cautelar: CautelarOpciones) =>
                (descripcion && descripcion.length) || cautelar === CautelarOpciones.SI,
              then: Yup.string().required(' ')
            })
            .when([FormField.NIG_FULL_NUMBER], {
              is: (item: string) => item && item.length,
              then: Yup.string()
                .required(' ')
                .min(7, ' ')
            })
        })
      case TipoMensaje.INICIADOR_EJECUCION:
        return Yup.object().shape({
          [FormField.NIG_FULL_NUMBER]: Yup.string()
            .min(19, t('form.validation.NIG'))
            .when(
              [
                FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL,
                FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION
              ],
              {
                is: (deTituloJudicial: boolean, orden: string) => {
                  orden = orden?.toUpperCase()
                  return (
                    deTituloJudicial === false &&
                    (orden === LiteralTiposOrden.SOCIAL || orden === LiteralTiposOrden.CIVIL)
                  )
                },
                then: Yup.string().notRequired(),
                otherwise: Yup.string().required(defaultRequiredText)
              }
            ),
          [FormField.NIG_1]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .min(5, ' ')
            .when(
              [
                FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL,
                FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION
              ],
              {
                is: (deTituloJudicial: boolean, orden: string) => {
                  orden = orden?.toUpperCase()
                  return (
                    deTituloJudicial === false &&
                    (orden === LiteralTiposOrden.SOCIAL || orden === LiteralTiposOrden.CIVIL)
                  )
                },
                then: Yup.string().notRequired(),
                otherwise: Yup.string().required(' ')
              }
            ),
          [FormField.NIG_2]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .min(2, ' ')
            .when(
              [
                FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL,
                FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION
              ],
              {
                is: (deTituloJudicial: boolean, orden: string) => {
                  orden = orden?.toUpperCase()
                  return (
                    deTituloJudicial === false &&
                    (orden === LiteralTiposOrden.SOCIAL || orden === LiteralTiposOrden.CIVIL)
                  )
                },
                then: Yup.string().notRequired(),
                otherwise: Yup.string().required(' ')
              }
            ),
          [FormField.NIG_3]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .min(1, ' ')
            .when(
              [
                FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL,
                FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION
              ],
              {
                is: (deTituloJudicial: boolean, orden: string) => {
                  orden = orden?.toUpperCase()
                  return (
                    deTituloJudicial === false &&
                    (orden === LiteralTiposOrden.SOCIAL || orden === LiteralTiposOrden.CIVIL)
                  )
                },
                then: Yup.string().notRequired(),
                otherwise: Yup.string().required(' ')
              }
            ),
          [FormField.NIG_4]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .min(4, ' ')
            .when(
              [
                FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL,
                FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION
              ],
              {
                is: (deTituloJudicial: boolean, orden: string) => {
                  orden = orden?.toUpperCase()
                  return (
                    deTituloJudicial === false &&
                    (orden === LiteralTiposOrden.SOCIAL || orden === LiteralTiposOrden.CIVIL)
                  )
                },
                then: Yup.string().notRequired(),
                otherwise: Yup.string().required(' ')
              }
            ),
          [FormField.NIG_5]: Yup.string()
            .matches(regexNumber, mustBeNumber)
            .min(7, ' ')
            .when(
              [
                FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL,
                FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION
              ],
              {
                is: (deTituloJudicial: boolean, orden: string) => {
                  orden = orden?.toUpperCase()
                  return (
                    deTituloJudicial === false &&
                    (orden === LiteralTiposOrden.SOCIAL || orden === LiteralTiposOrden.CIVIL)
                  )
                },
                then: Yup.string().notRequired(),
                otherwise: Yup.string().required(' ')
              }
            )
        })
      default:
        return undefined
    }
  }

  // VALIDACIONES INTERVINIENTES
  const intervinientes = () => {
    if (tipoMensaje === TipoMensaje.MONITORIO) {
      return Yup.object().shape({
        [FormField.INTERVINIENTES]: Yup.array()
          .required(t('form.validation.conditions_intervinientes_monitorio'))
          .min(1, t('form.validation.conditions_intervinientes_monitorio'))
          .test(
            'hasDDOandSOL',
            t('form.validation.conditions_intervinientes_monitorio'),
            (value: any) => {
              return value?.length > 0
                ? Boolean(
                    value?.some((item: Interviniente) => item.intervencionTipo?.codigo === 'DDO') &&
                      value?.some((item: Interviniente) => item.intervencionTipo?.codigo === 'SOL')
                  )
                : false
            }
          )
      })
    } else if (tipoMensaje !== TipoMensaje.ESCRITO) {
      return Yup.object().shape({
        [FormField.INTERVINIENTES]: Yup.array()
          .required(defaultRequiredText)
          .min(1, t('form.validation.one_interviniente'))
      })
    }
    return undefined
  }

  const intervinientesFromLexon = () => {
    return Yup.object().shape({
      [FormField.INTERVINIENTES_FROM_LEXON_VALIDATED]: Yup.boolean()
        .required()
        .test('is-true', 'Must be true', value => value === true)
    })
  }

  // VALIDACIONES DATOS
  const DATOS_INICIADORES_SHARED = {
    [FormField.DATOS_MATERIA]: Yup.object()
      .nullable()
      .when([FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION, FormField.ORGANO_DESTINO], {
        is: (tipoOrden: string, _organo) => {
          // const claseRegistro = organo?.organoDestinoOrganoJudicial?.claseRegistro
          tipoOrden = tipoOrden?.toUpperCase()
          // return (
          //   !claseRegistro &&
          //   tipoOrden !== LiteralTiposOrden.PENAL &&
          //   tipoOrden !== LiteralTiposOrden.MILITAR &&
          //   tipoOrden !== LiteralTiposOrden.CONSTITUCIONAL
          // )
          return (
            tipoOrden !== LiteralTiposOrden.PENAL &&
            tipoOrden !== LiteralTiposOrden.MILITAR &&
            tipoOrden !== LiteralTiposOrden.CONSTITUCIONAL
          )
        },
        then: Yup.object()
          .nullable()
          .required(defaultRequiredText)
      }),
    [FormField.DATOS_DELITO]: Yup.object()
      .nullable()
      .when([FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION, FormField.ORGANO_DESTINO], {
        is: (tipoOrden: string, organo) => {
          const claseRegistro = organo?.organoDestinoOrganoJudicial?.claseRegistro
          tipoOrden = tipoOrden?.toUpperCase()
          return !claseRegistro && tipoOrden === LiteralTiposOrden.PENAL
        },
        then: Yup.object()
          .nullable()
          .required(defaultRequiredText)
      }),
    [FormField.DATOS_TIPO_CUANTIA]: Yup.object()
      .nullable()
      .when([FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION], {
        is: (item: string) => {
          const options = [
            LiteralTiposOrden.CIVIL,
            LiteralTiposOrden.CONTENCIOSO,
            LiteralTiposOrden.SOCIAL
          ]
          return item && options.includes(item.toUpperCase() as LiteralTiposOrden)
        },
        then: Yup.object()
          .nullable()
          .required(defaultRequiredText)
      }),
    [FormField.DATOS_CUANTIA_PRINCIPAL]: Yup.string()
      .nullable()
      .when([FormField.DATOS_TIPO_CUANTIA], {
        is: (item: TipoCuantia) => item && item.codigo === 'D',
        then: Yup.string()
          .nullable()
          .required(defaultRequiredText)
          .matches(regexDecimals(), cuantiaReconvencionText(CUANTIA_LENGTH))
          .test('greater-than-zero', greaterOrEqualOne, value => {
            return Number(value) > 0
          })
      }),
    [FormField.DATOS_FECHA_ACUERDO]: Yup.date()
      .nullable()
      .typeError(invalidDate)
      .when([FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION], {
        is: (item: string) => item && item.toUpperCase() === LiteralTiposOrden.CONTENCIOSO,
        then: Yup.date()
          .nullable()
          .min(new Date(MIN_DATE), invalidDate)
          .max(addOneDay(), notSuperiorToCurrentDate)
          .typeError(invalidDate)
      }),
    [FormField.DATOS_HORA]: Yup.date()
      .nullable()
      .typeError(invalidTime),
    [FormField.DATOS_FECHA]: Yup.date()
      .nullable()
      .typeError(invalidDate)
      .when([FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION], {
        is: (item: string) => item && item.toUpperCase() === LiteralTiposOrden.PENAL,
        then: Yup.date()
          .nullable()
          .min(new Date(MIN_DATE), invalidDate)
          .max(addOneDay(), notSuperiorToCurrentDate)
          .typeError(invalidDate)
      }),
    [FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN]: Yup.string().matches(
      regexYear,
      t('form.validation.must_be_year')
    ),
    [FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN]: Yup.string()
      .nullable()
      .matches(regexNumber, mustBeNumber)
      .when([FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN], {
        is: (item: string) => item && item.length > 0,
        then: Yup.string()
          .nullable()
          .required(defaultRequiredText)
      }),
    [FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN]: Yup.string()
      .nullable()
      .when([FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN], {
        is: (item: string) => item && item.length > 0,
        then: Yup.string()
          .nullable()
          .required(defaultRequiredText)
          .matches(regexYear, mustBeYear)
      })
  }

  const datos = () => {
    switch (tipoMensaje) {
      case TipoMensaje.INICIADOR_ASUNTO:
        return Yup.object().shape(
          {
            ...DATOS_INICIADORES_SHARED,
            [FormField.DATOS_OBJETO_RECURSO]: Yup.string()
              .nullable()
              .when([FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION], {
                is: (item: string) => item && item.toUpperCase() === LiteralTiposOrden.CONTENCIOSO,
                then: Yup.string()
                  .nullable()
                  .matches(regexExcludedCharacters, excludedCharacters)
              }),
            [FormField.DATOS_ORGANISMO]: Yup.string()
              .nullable()
              .when([FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION], {
                is: (item: string) => item && item.toUpperCase() === LiteralTiposOrden.CONTENCIOSO,
                then: Yup.string()
                  .nullable()
                  .required(defaultRequiredText)
                  .matches(regexExcludedCharacters, excludedCharacters)
              }),
            [FormField.ORGANO_ORIGEN_ORGANISMO_ORIGEN]: Yup.object()
              .nullable()
              .when([FormField.ORGANO_ORIGEN_TIPO_ORIGEN], {
                is: (item: string) => item && item === TIPO_ORIGEN.EXTRAJUDICIAL,
                then: Yup.object()
                  .nullable()
                  .required(defaultRequiredText)
              }),
            [FormField.ORGANO_ORIGEN_NUMERO_EXPEDIENTE]: Yup.string()
              .nullable()
              .when([FormField.ORGANO_ORIGEN_TIPO_ORIGEN], {
                is: (item: string) => item && item === TIPO_ORIGEN.EXTRAJUDICIAL,
                then: Yup.string()
                  .nullable()
                  .required(defaultRequiredText)
              }),
            [FormField.ORGANO_ORIGEN_YEAR_EXPEDIENTE]: Yup.string()
              .nullable()
              .when([FormField.ORGANO_ORIGEN_TIPO_ORIGEN], {
                is: (item: string) => item && item === TIPO_ORIGEN.EXTRAJUDICIAL,
                then: Yup.string()
                  .nullable()
                  .required(defaultRequiredText)
                  .matches(regexYear, mustBeYear)
              })
          },
          [
            [FormField.ORGANO_ORIGEN_NUMERO_EXPEDIENTE, FormField.ORGANO_ORIGEN_YEAR_EXPEDIENTE],
            [FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN, FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN]
          ]
        )
      case TipoMensaje.INICIADOR_EJECUCION:
        return Yup.object().shape(
          {
            ...DATOS_INICIADORES_SHARED,
            [FormField.DATOS_ORGANISMO_ORIGEN]: Yup.mixed()
              .nullable()
              .when(
                [
                  FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL,
                  FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION
                ],
                {
                  is: (deTituloJudicial: boolean, orden: string) => {
                    orden = orden?.toUpperCase()
                    return deTituloJudicial === false && orden === LiteralTiposOrden.SOCIAL
                  },
                  then: Yup.mixed()
                    .nullable()
                    .required(defaultRequiredText)
                }
              ),
            [FormField.DATOS_CUANTIA_INTERESES]: Yup.string()
              .nullable()
              .matches(regexUnlimitedDecimals, unlimitedDecimals(10)),
            [FormField.DATOS_CUANTIA_COSTAS]: Yup.string()
              .nullable()
              .matches(regexUnlimitedDecimals, unlimitedDecimals(10))
          },
          [[FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN, FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN]]
        )
      case TipoMensaje.MONITORIO:
        return Yup.object().shape(
          {
            ...DATOS_INICIADORES_SHARED,
            [FormField.DATOS_CUANTIA_PRINCIPAL]: Yup.string()
              .nullable()
              .when([FormField.DATOS_TIPO_CUANTIA], {
                is: (item: TipoCuantia) => item && item.codigo === 'D',
                then: Yup.string()
                  .nullable()
                  .required(defaultRequiredText)
                  .matches(regexDecimals(), cuantiaReconvencionText(CUANTIA_LENGTH))
              }),
            [FormField.DATOS_CUANTIA_INTERESES_REMUNERATORIOS]: Yup.string()
              .nullable()
              .when([FormField.DATOS_TIPO_CUANTIA], {
                is: (item: TipoCuantia) => item && item.codigo === 'D',
                then: Yup.string()
                  .nullable()
                  .required(defaultRequiredText)
                  .matches(regexDecimals(), cuantiaReconvencionText(CUANTIA_LENGTH))
              }),
            [FormField.DATOS_CUANTIA_INTERESES_DEMORA]: Yup.string()
              .nullable()
              .when([FormField.DATOS_TIPO_CUANTIA], {
                is: (item: TipoCuantia) => item && item.codigo === 'D',
                then: Yup.string()
                  .nullable()
                  .required(defaultRequiredText)
                  .matches(regexDecimals(), cuantiaReconvencionText(CUANTIA_LENGTH))
              }),
            [FormField.DATOS_CUANTIA_COMISION_IMPAGO_RECIBOS]: Yup.string()
              .nullable()
              .when([FormField.DATOS_TIPO_CUANTIA], {
                is: (item: TipoCuantia) => item && item.codigo === 'D',
                then: Yup.string()
                  .nullable()
                  .required(defaultRequiredText)
                  .matches(regexDecimals(), cuantiaReconvencionText(CUANTIA_LENGTH))
              }),
            [FormField.DATOS_CUANTIA_PENALIZACION_RETRASO_PAGO]: Yup.string()
              .nullable()
              .when([FormField.DATOS_TIPO_CUANTIA], {
                is: (item: TipoCuantia) => item && item.codigo === 'D',
                then: Yup.string()
                  .nullable()
                  .required(defaultRequiredText)
                  .matches(regexDecimals(), cuantiaReconvencionText(CUANTIA_LENGTH))
              }),
            [FormField.DATOS_ORIGEN_DEUDA]: Yup.string()
              .nullable()
              .required(defaultRequiredText),
            [FormField.DATOS_PETICION]: Yup.string()
              .nullable()
              .required(defaultRequiredText)
          },
          [[FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN, FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN]]
        )

      default:
        return Yup.object().shape({
          [FormField.DATOS_TIPO_CUANTIA]: Yup.object()
            .nullable()
            .when([FormField.DATOS_RECONVENCION], {
              is: (item: string) => item === 'Sí',
              then: Yup.object()
                .nullable()
                .required(defaultRequiredText)
            }),
          [FormField.DATOS_CUANTIA_RECONVENCION]: Yup.string()
            .nullable()
            .when([FormField.DATOS_TIPO_CUANTIA], {
              is: (item: TipoCuantia) => item && item.codigo === 'D',
              then: Yup.string()
                .nullable()
                .required(defaultRequiredText)
                .matches(regexDecimals(), cuantiaReconvencionText(CUANTIA_LENGTH))
                .test('greater-than-zero', greaterOrEqualOne, value => {
                  return Number(value) > 0
                })
            })
        })
    }
  }

  // VALIDACIONES ORIGEN
  const origen = () => {
    switch (tipoMensaje) {
      case TipoMensaje.INICIADOR_EJECUCION:
        return Yup.object().shape({
          [FormField.ORGANO_ORIGEN_DESCRIPCION]: Yup.string().when(
            [
              FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL,
              FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION
            ],
            {
              is: (deTituloJudicial: boolean, orden: string) => {
                orden = orden?.toUpperCase()
                return (
                  deTituloJudicial === false &&
                  (orden === LiteralTiposOrden.SOCIAL || orden === LiteralTiposOrden.CIVIL)
                )
              },
              then: Yup.string().notRequired(),
              otherwise: Yup.string().required(defaultRequiredText)
            }
          ),
          [FormField.ORGANO_ORIGEN_ORDEN_DESCRIPCION]: Yup.string().when(
            [
              FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL,
              FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION
            ],
            {
              is: (deTituloJudicial: boolean, orden: string) => {
                orden = orden?.toUpperCase()
                return (
                  deTituloJudicial === false &&
                  (orden === LiteralTiposOrden.SOCIAL || orden === LiteralTiposOrden.CIVIL)
                )
              },
              then: Yup.string().notRequired(),
              otherwise: Yup.string().required(defaultRequiredText)
            }
          )
        })
      case TipoMensaje.PERSONACION:
        return Yup.object().shape({
          [FormField.ORGANO_ORIGEN_DESCRIPCION]: Yup.string().required(defaultRequiredText),
          [FormField.ORGANO_ORIGEN_ORDEN_DESCRIPCION]: Yup.string().required(defaultRequiredText)
        })
      default:
        return Yup.object().shape({
          [FormField.ORGANO_ORIGEN_DESCRIPCION]: Yup.string().when([FormField.CAUTELAR], {
            is: (item: string) => item === CautelarOpciones.SI,
            then: Yup.string().required(defaultRequiredText)
          }),
          [FormField.ORGANO_ORIGEN_ORDEN_DESCRIPCION]: Yup.string().when([FormField.CAUTELAR], {
            is: (item: string) => item === CautelarOpciones.SI,
            then: Yup.string().required(defaultRequiredText)
          })
        })
    }
  }

  const validations = Yup.object().shape({
    // VALIDACIONES REMITENTE
    // [FormField.REMITENTE_TITULAR]: Yup.string().required(defaultRequiredText),
    // VALIDACIONES DESTINO
    [FormField.ORGANO_DESTINO_DESCRIPCION]: Yup.string().required(defaultRequiredText),
    [FormField.ORGANO_DESTINO_ORDEN_DESCRIPCION]: Yup.string().required(defaultRequiredText),
    // VALIDACIONES RESOLUCION
    [FormField.TIPO_RESOLUCION]: Yup.object()
      .nullable()
      .when([FormField.RESOLUCION], {
        is: (item: string) => item === ResolucionOpciones.SI,
        then: Yup.object()
          .nullable()
          .required(defaultRequiredText)
      }),
    [FormField.FECHA_RESOLUCION]: Yup.date()
      .nullable()
      .typeError(invalidDate)
      .when([FormField.RESOLUCION], {
        is: (item: string) => item === ResolucionOpciones.SI,
        then: Yup.date()
          .nullable()
          .required(defaultRequiredText)
          .typeError(invalidDate)
          .max(addOneDay(), notSuperiorToCurrentDate)
      }),
    [FormField.NUMERO_RESOLUCION]: Yup.string()
      .nullable()
      .when([FormField.RESOLUCION], {
        is: (item: string) => item === ResolucionOpciones.SI,
        then: Yup.string()
          .nullable()
          .required(defaultRequiredText)
          .matches(regexNumberOrDot, mustBeNumber)
      }),
    [FormField.YEAR_RESOLUCION]: Yup.string()
      .nullable()
      .when([FormField.RESOLUCION], {
        is: (item: string) => item === ResolucionOpciones.SI,
        then: Yup.string()
          .nullable()
          .required(defaultRequiredText)
          .matches(regexYear, mustBeYear)
      })
  })

  return merge(
    adjuntos(),
    NIG(),
    procedimiento(),
    datos(),
    intervinientes(),
    intervinientesFromLexon(),
    origen(),
    validations
  )
}

export default useEcritoLegalValidation
